import { useEffect, useState } from "react";
import api, { apiError, fmtDate } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, Circle, ListChecks, Zap } from "lucide-react";

const FASE_LABEL = { pubblicazione: "Pubblicazione", proposta: "Proposta d'acquisto", preliminare: "Preliminare", rogito: "Rogito e chiusura" };

function Voce({ v, fase, canEdit, onToggle }) {
  const Icon = v.fatto ? CheckCircle2 : Circle;
  const editable = canEdit && !v.auto;
  return (
    <button type="button" data-testid={`ct-${fase}-${v.id}`} disabled={!editable} onClick={() => onToggle(fase, v)}
      className={`w-full flex items-start gap-2.5 p-2.5 rounded-xl border text-left transition-colors ${v.fatto ? "border-emerald-200 bg-emerald-50/60" : "border-[var(--border)] bg-white"} ${editable ? "hover:border-[#FC82ED]/50" : "cursor-default"}`}>
      <Icon size={18} className={`shrink-0 mt-0.5 ${v.fatto ? "text-emerald-500" : "text-slate-300"}`} />
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium break-words">{v.label}</div>
        <div className="text-[11px] text-[var(--muted)] flex flex-wrap gap-x-2">
          {v.auto && <span className="inline-flex items-center gap-1 text-[#9A6A00]"><Zap size={11} /> automatica</span>}
          {v.info && <span>{v.info}</span>}
          {!v.auto && v.fatto && v.by && <span>da {v.by} · {fmtDate(v.at)}</span>}
        </div>
      </div>
    </button>
  );
}

export default function ChecklistTappe({ id }) {
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(null);
  const load = () => api.get(`/immobili/${id}/checklist-tappe`).then((r) => { setD(r.data); setOpen((o) => o || r.data.fase_corrente || "pubblicazione"); }).catch(() => setD(null));
  useEffect(() => { load(); }, [id]);
  if (!d) return null;
  const toggle = async (fase, v) => {
    try { const r = await api.post(`/immobili/${id}/checklist-tappe`, { fase, voce: v.id, fatto: !v.fatto }); setD(r.data); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="card-immo p-5 mb-6" data-testid="checklist-tappe">
      <div className="flex items-center gap-2 mb-1">
        <span className="w-8 h-8 rounded-xl bg-[#FC82ED]/15 text-[#FC82ED] flex items-center justify-center"><ListChecks size={16} /></span>
        <h2 className="font-display font-bold text-base">Checklist di tappa</h2>
      </div>
      <p className="text-xs text-[var(--muted)] mb-4">{d.puo_modificare ? "Spunta le voci man mano. Quelle automatiche si aggiornano da sole quando il dato c'è." : "Sola lettura: le voci le spunta la Segreteria."}</p>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 mb-4">
        {d.fasi.map((f) => (
          <button key={f.fase} data-testid={`ct-tab-${f.fase}`} onClick={() => setOpen(f.fase)}
            className={`p-3 rounded-xl border text-left transition-colors ${open === f.fase ? "border-[#FC82ED] bg-[#FC82ED]/5" : "border-[var(--border)] hover:bg-slate-50"}`}>
            <div className="text-xs font-semibold truncate">{FASE_LABEL[f.fase]}{d.fase_corrente === f.fase && <span className="ml-1 text-[#FC82ED]">•</span>}</div>
            <div className="flex items-center gap-2 mt-1.5">
              <div className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden"><div className={`h-full rounded-full ${f.pct === 100 ? "bg-emerald-500" : "bg-[#FC82ED]"}`} style={{ width: `${f.pct}%` }} /></div>
              <span data-testid={`ct-pct-${f.fase}`} className="text-[11px] font-bold mono">{f.pct}%</span>
            </div>
          </button>
        ))}
      </div>
      {d.fasi.filter((f) => f.fase === open).map((f) => (
        <div key={f.fase} className="space-y-2" data-testid={`ct-voci-${f.fase}`}>
          {f.voci.map((v) => <Voce key={v.id} v={v} fase={f.fase} canEdit={d.puo_modificare} onToggle={toggle} />)}
        </div>
      ))}
    </div>
  );
}
